import {
  createContext,
  useState,
  useContext,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useSlideStore } from "../store/useSlideStore";
import { useSessionStore } from "../store/useSessionStore";
import { useBiteStatusStore } from "../store/useBiteStatusStore";
import { tutorApi } from "@/utils/axios-utils";
import { debugLog, debugWarn } from "@/utils/debugLog";

type CooldownType = {
  label: string;
  timeMs: number;
};

export const cooldownMap: Record<string, CooldownType> = {
  "0": {
    label: "1 minute",
    timeMs: 60000,
  },
  "1": {
    label: "2 minutes",
    timeMs: 120000,
  },
  "2": {
    label: "5 minutes",
    timeMs: 300000,
  },
};

export const getCooldownForLevel = (level: number): CooldownType => {
  return cooldownMap[level.toString()] || cooldownMap["0"];
};

type CooldownInfo = {
  level: number;
  timeMs: number;
  label: string;
};

interface CooldownContextType {
  cooldownInfo: CooldownInfo;
  isOnCooldown: boolean;
  resetCooldown: () => void;
  releaseCooldown: () => void;
  startCooldown: () => void;
  cooldownEnabled: boolean;
  setCooldownEnabled: (enabled: boolean) => void;
  cooldownSlideIndex: number;
  setCooldownSlideIndex: (slideIndex: number) => void;
  incrementCooldownTime: (additionalMs: number) => void;
  getCurrentCooldownLabel: () => string;
  initializeCompletionData: (completedIndices: number[]) => void;
  shouldBypassCooldown: boolean;
}

const CooldownContext = createContext<CooldownContextType | undefined>(
  undefined,
);

const COOLDOWN_STORAGE_KEY = "tutor.next_slide.cooldown_state";

type CooldownState = {
  level: number;
  endAt: number | null;
  slideIndex: number;
};

const readCooldownState = (): CooldownState | null => {
  const raw = localStorage.getItem(COOLDOWN_STORAGE_KEY);
  return raw ? JSON.parse(raw) : null;
};

const writeCooldownState = (state: Partial<CooldownState>): void => {
  const existing = readCooldownState() ?? {
    level: 0,
    endAt: null,
    slideIndex: 0,
  };
  localStorage.setItem(
    COOLDOWN_STORAGE_KEY,
    JSON.stringify({ ...existing, ...state }),
  );
};

export const CooldownProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [cooldownEnabled, setCooldownEnabled] = useState(false);
  const [cooldownLevel, setCooldownLevel] = useState(0);
  const [isOnCooldown, setIsOnCooldown] = useState(false);
  const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const currentSlideIndex = useSlideStore((state) => state.currentSlideIndex);
  const [cooldownSlideIndex, setCooldownSlideIndex] = useState(0);
  const sessionId = useSessionStore((state) => state.sessionId);
  const previousSessionIdRef = useRef<string | null>(null);

  // Completion tracking for cooldown bypass — populated from backend, never shrinks
  const [completedBites, setCompletedBites] = useState<Set<number>>(new Set());
  const completedBitesRef = useRef(completedBites);
  completedBitesRef.current = completedBites;
  const currentLessonId = useSlideStore((state) => state.currentLessonId);

  const cooldownInfo: CooldownInfo = useMemo(() => {
    const currentCooldown =
      cooldownMap[cooldownLevel.toString()] || cooldownMap["0"];
    return {
      level: cooldownLevel,
      timeMs: currentCooldown.timeMs,
      label: currentCooldown.label,
    };
  }, [cooldownLevel]);

  // Cooldown bypass: true if this slide was completed (per backend analytics)
  const shouldBypassCooldown = useMemo(
    () => completedBites.has(currentSlideIndex),
    [completedBites, currentSlideIndex],
  );

  // Merge incoming completed indices — never removes entries
  const initializeCompletionData = useCallback((completedIndices: number[]) => {
    debugLog("[CooldownContext] initializeCompletionData called", {
      completedIndices,
    });
    if (completedIndices.length === 0) return;
    setCompletedBites((prev) => {
      const merged = new Set(prev);
      completedIndices.forEach((i) => merged.add(i));
      return merged;
    });
    // Cancel any active cooldown whose slide is now in the completed set.
    // Reads cooldownSlideIndex from localStorage so it works even when restored
    // from a previous session before React state is populated.
    const stored = readCooldownState();
    const latestSlideIndex = useSlideStore.getState().currentSlideIndex;
    const cooldownIsForCompletedSlide =
      completedIndices.includes(latestSlideIndex) ||
      (stored?.slideIndex != null &&
        completedIndices.includes(stored.slideIndex));
    if (cooldownIsForCompletedSlide) {
      debugLog(
        "[CooldownContext] Active cooldown is for a completed slide, cancelling",
        {
          currentSlideIndex: latestSlideIndex,
          storedSlideIndex: stored?.slideIndex,
        },
      );
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
      setIsOnCooldown(false);
      writeCooldownState({ endAt: null });
    }
  }, []);

  // Poll backend every 30s to pick up Lambda writes.
  // Stop polling once every bite in the lesson has been completed.
  const slidesLength = useSlideStore((state) => state.slides.length);
  const allBitesCompleted =
    slidesLength > 0 && completedBites.size >= slidesLength;

  // The launch classroom picks which round of the lesson the server reports
  // progress for; without it the server falls back to its own default.
  const [searchParams] = useSearchParams();
  const classroomId = Number(searchParams.get("classroomId")) || undefined;

  useEffect(() => {
    if (!currentLessonId || isNaN(Number(currentLessonId))) return;
    if (allBitesCompleted) return;
    const poll = async () => {
      try {
        const response = await tutorApi.get(
          `/session/completed_bites/${currentLessonId}`,
          {
            skipAuthRedirect: true,
            ...(classroomId && { params: { classroom_id: classroomId } }),
          },
        );
        const indices: number[] = response.data?.completed_bite_indices ?? [];
        if (indices.length > 0) {
          initializeCompletionData(indices);
        }
      } catch (e) {
        debugWarn("[CooldownContext] Failed to poll completed bites", e);
      }
    };
    const intervalId = setInterval(poll, 30000);
    return () => clearInterval(intervalId);
  }, [
    currentLessonId,
    initializeCompletionData,
    allBitesCompleted,
    classroomId,
  ]);

  // Poll bite_metrics every 30s to upgrade heuristic statuses with accurate Lambda LLM statuses.
  // Lambda DDB Stream path writes to PostgreSQL; this syncs the frontend store.
  // Stops once isInitialized is false (session reset) or all bites completed.
  const biteStatusIsInitialized = useBiteStatusStore((s) => s.isInitialized);
  useEffect(() => {
    if (!currentLessonId || isNaN(Number(currentLessonId))) return;
    if (!biteStatusIsInitialized) return;
    if (allBitesCompleted) return;
    const poll = async () => {
      try {
        const response = await tutorApi.get(
          `/session/bite_metrics/${currentLessonId}`,
          {
            skipAuthRedirect: true,
            ...(classroomId && { params: { classroom_id: classroomId } }),
          },
        );
        const biteMetrics = response.data?.bite_metrics ?? {};
        if (Object.keys(biteMetrics).length > 0) {
          useBiteStatusStore.getState().applyBiteMetrics(biteMetrics);
        }
      } catch (e) {
        debugWarn("[CooldownContext] Failed to poll bite_metrics", e);
      }
    };
    const intervalId = setInterval(poll, 30000);
    return () => clearInterval(intervalId);
  }, [
    currentLessonId,
    biteStatusIsInitialized,
    allBitesCompleted,
    classroomId,
  ]);

  const incrementCooldown = useCallback(() => {
    setCooldownLevel((prev) => {
      const newLevel = Math.min(prev + 1, 2);
      writeCooldownState({ level: newLevel, endAt: null });
      return newLevel;
    });
  }, []);

  const incrementCooldownRef = useRef(incrementCooldown);
  incrementCooldownRef.current = incrementCooldown;

  const resetCooldown = useCallback(() => {
    const latestSlideIndex = useSlideStore.getState().currentSlideIndex;
    setCooldownLevel(0);
    setIsOnCooldown(false);
    setCooldownSlideIndex(latestSlideIndex + 1);
    if (cooldownTimerRef.current) {
      clearTimeout(cooldownTimerRef.current);
      cooldownTimerRef.current = null;
    }
    writeCooldownState({
      level: 0,
      endAt: null,
      slideIndex: latestSlideIndex + 1,
    });
  }, []);

  // Open the gate without claiming we moved on. resetCooldown also zeroes the
  // escalation ladder and re-points the stored slideIndex at the next bite —
  // and that stored index is read back as truth by initializeCompletionData.
  const releaseCooldown = useCallback(() => {
    setIsOnCooldown(false);
    if (cooldownTimerRef.current) {
      clearTimeout(cooldownTimerRef.current);
      cooldownTimerRef.current = null;
    }
    writeCooldownState({ endAt: null });
  }, []);

  const startCooldown = useCallback(() => {
    const latestSlideIndex = useSlideStore.getState().currentSlideIndex;
    const bypass = completedBitesRef.current.has(latestSlideIndex);

    debugLog("[CooldownContext] startCooldown called", {
      latestSlideIndex,
      bypass,
      completed: [...completedBitesRef.current],
      cooldownLevel,
      cooldownTimeMs: cooldownInfo.timeMs,
    });

    // Bypass cooldown if this slide was already completed
    if (bypass) {
      debugLog(
        "[CooldownContext] Cooldown bypassed - slide already completed",
        { latestSlideIndex },
      );
      setIsOnCooldown(false);
      return;
    }

    if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);

    if (cooldownInfo.timeMs > 0) {
      setIsOnCooldown(true);
      const endAt = Date.now() + cooldownInfo.timeMs;
      writeCooldownState({
        level: cooldownInfo.level,
        endAt,
        slideIndex: latestSlideIndex,
      });

      debugLog(
        "[CooldownContext] Cooldown started, will end at:",
        new Date(endAt).toISOString(),
      );

      cooldownTimerRef.current = setTimeout(() => {
        debugLog("[CooldownContext] Cooldown expired");
        setIsOnCooldown(false);
        cooldownTimerRef.current = null;
        incrementCooldownRef.current();
        writeCooldownState({ endAt: null });
      }, cooldownInfo.timeMs);
    }
    setCooldownSlideIndex(latestSlideIndex);
  }, [cooldownInfo.timeMs, cooldownInfo.level, cooldownLevel]);

  const incrementCooldownTime = useCallback((additionalMs: number) => {
    const state = readCooldownState();
    if (!state || !state.endAt) {
      debugLog("[CooldownContext] No active cooldown to increment");
      return;
    }

    const now = Date.now();
    if (state.endAt <= now) {
      debugLog("[CooldownContext] Cooldown already expired, not incrementing");
      return;
    }

    // Add additional time to the existing endAt
    const newEndAt = state.endAt + additionalMs;
    debugLog(
      "[CooldownContext] Incrementing cooldown by",
      additionalMs / 1000,
      "seconds",
      {
        oldEndAt: new Date(state.endAt).toISOString(),
        newEndAt: new Date(newEndAt).toISOString(),
      },
    );

    // Update localStorage
    writeCooldownState({
      level: state.level,
      endAt: newEndAt,
      slideIndex: state.slideIndex,
    });

    // Clear existing timer and set new one
    if (cooldownTimerRef.current) {
      clearTimeout(cooldownTimerRef.current);
    }

    const remaining = newEndAt - now;
    cooldownTimerRef.current = setTimeout(() => {
      setIsOnCooldown(false);
      cooldownTimerRef.current = null;
      incrementCooldownRef.current();
      writeCooldownState({ endAt: null });
    }, remaining);
  }, []);

  const getCurrentCooldownLabel = useCallback(() => {
    const state = readCooldownState();
    if (!state || !state.endAt) {
      return cooldownInfo.label;
    }

    const now = Date.now();
    const remainingMs = Math.max(0, state.endAt - now);
    const remainingSeconds = Math.ceil(remainingMs / 1000);

    // Determine appropriate label based on remaining time
    if (remainingSeconds >= 300) {
      // 5 minutes or more
      return "5 minutes";
    } else if (remainingSeconds >= 120) {
      // 2 minutes or more
      return "2 minutes";
    } else if (remainingSeconds >= 60) {
      // 1 minute or more
      return "1 minute";
    } else if (remainingSeconds > 0) {
      // Less than 1 minute
      return `${remainingSeconds} seconds`;
    }

    return cooldownInfo.label;
  }, [cooldownInfo.label]);

  // Reset cooldown level to 0 when session changes
  useEffect(() => {
    if (
      sessionId &&
      sessionId !== previousSessionIdRef.current &&
      previousSessionIdRef.current !== null
    ) {
      // Only reset if this is a CHANGE (not initial mount)
      debugLog(
        "[CooldownContext] Session changed, resetting cooldown to level 0",
        {
          previousSessionId: previousSessionIdRef.current,
          newSessionId: sessionId,
        },
      );

      // Clear any active cooldown timer
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }

      // Reset to level 0
      setCooldownLevel(0);
      setIsOnCooldown(false);
      writeCooldownState({
        level: 0,
        endAt: null,
        slideIndex: 0,
      });
    }

    // Update the ref after handling the change
    if (sessionId) {
      previousSessionIdRef.current = sessionId;
    }
  }, [sessionId]);

  useEffect(() => {
    const parsed = readCooldownState();
    if (!parsed) return;

    if (typeof parsed.level === "number") {
      setCooldownLevel(Math.max(0, Math.min(parsed.level, 2)));
    }
    if (typeof parsed.slideIndex === "number") {
      setCooldownSlideIndex(parsed.slideIndex);
    }

    const now = Date.now();
    if (parsed.endAt && parsed.endAt > now) {
      const remaining = parsed.endAt - now;
      setIsOnCooldown(true);
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
      cooldownTimerRef.current = setTimeout(() => {
        setIsOnCooldown(false);
        cooldownTimerRef.current = null;
        incrementCooldownRef.current();
        writeCooldownState({ endAt: null });
      }, remaining);
    } else if (parsed.endAt && parsed.endAt <= now) {
      const base = typeof parsed.level === "number" ? parsed.level : 0;
      const newLevel = Math.min(base + 1, 2);
      setCooldownLevel(newLevel);
      setIsOnCooldown(false);
      writeCooldownState({
        level: newLevel,
        endAt: null,
        slideIndex: parsed.slideIndex ?? 0,
      });
    }

    return () => {
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }
    };
  }, []);

  return (
    <CooldownContext.Provider
      value={{
        cooldownInfo,
        isOnCooldown,
        resetCooldown,
        releaseCooldown,
        startCooldown,
        cooldownEnabled,
        setCooldownEnabled,
        cooldownSlideIndex,
        setCooldownSlideIndex,
        incrementCooldownTime,
        getCurrentCooldownLabel,
        initializeCompletionData,
        shouldBypassCooldown,
      }}
    >
      {children}
    </CooldownContext.Provider>
  );
};

export const useCooldown = () => {
  const context = useContext(CooldownContext);
  if (context === undefined) {
    throw new Error("useCooldown must be used within a CooldownProvider");
  }
  return context;
};
